import { TurnRightOutlined, VisibilityOutlined } from '@mui/icons-material';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import {
  Box,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  Skeleton,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { format } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import loader from 'src/components/LoaderImg/loader.gif';
import useCurrentUser from 'src/hooks/use-current-user';
import { paths, permissions } from 'src/paths';
import { editSignDocAction, getAllCompletedDocAction, undoDocumentSignAction } from 'src/redux/actions/documentationAction';
import { Scrollbar } from '../scrollbar';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { singleSignatureWorkflow } from 'src/config';
interface CompletedDocumentProps {
  activeTab: string;
  completedDocuments: Array<string>;
  documentationDoc: any;
  setDocumentationDoc: any;
  openPrintMode: any;
  setIsFilterApply: any;
  abortController: any;
}

const columns: any = [
  { id: 'documentType', label: 'Document Type' },
  { id: 'dateOfService', label: 'Date of Service' },
  { id: 'therapistSignature', label: 'Therapist Signature' },
  { id: 'supervisiorSignature', label: 'Supervisior Signature' },
  { id: 'physicianSignature', label: 'Physician Signature' },
  { id: 'action', label: '', align: 'right' },
];

const notSortingHeader = [
  'therapistSignature',
  'supervisiorSignature',
  'physicianSignature',
  'action',
];

const CompletedDocument: FC<CompletedDocumentProps> = (props: CompletedDocumentProps) => {
  const {
    activeTab,
    completedDocuments,
    documentationDoc,
    setDocumentationDoc,
    openPrintMode,
    setIsFilterApply,
    abortController,
  } = props;
  const { state } = useLocation();
  const dispatch = useDispatch();
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const episodeOfCareId = state?.episodeOfCareId;
  const getAllSubmittedDocumentResponse = useSelector(
    (state: any) => state.getAllSubmittedDocumentReducer
  );
  const [isSingleSignatureWorkflow] = useFeatureToggleWhenReady(singleSignatureWorkflow, 'ENTERPRISE')

  const [providerId, discipline] = useCurrentUser(['providerId', 'discipline']);
  const getAllCompletedDoc = useSelector((state: any) => state.getAllCompetedDocReducer);

  //pagination logic

  const [rowPerPage, setRowPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('dateOfService');
  const [sortOrderArray, setSortArray] = useState<any>(['desc', 'desc', 'desc', 'desc', 'desc']);

  const onPageChange = (event: any, newPage: number) => {
    dispatch(
      getAllCompletedDocAction({
        therapistUuid: providerId,
        eocId: state?.episodeOfCareId,
        signal: abortController,
        params: {
          type: activeTab,
          filter: 'COMPLETED',
          sortDirection: getSortOrder(sortBy),
          sortBy: sortBy,
          page: newPage,
          pageSize: rowPerPage,
        },
      })
    );
    setPage(newPage);
  };

  const getSortOrder = (sortColumn: string) => {
    switch (sortColumn) {
      case 'documentType':
        return sortOrderArray[0];
      case 'dateOfService':
        return sortOrderArray[1];
      default:
        return '';
    }
  };

  const getSortOrder1 = (sortColumn: string, newSortOrderArray: any) => {
    switch (sortColumn) {
      case 'documentType':
        return newSortOrderArray[0];
      case 'dateOfService':
        return newSortOrderArray[1];
      default:
        return '';
    }
  };

  const onRowsPerPageChange = (event: any) => {
    const newRowsPerPage = event.target.value;
    setRowPerPage(newRowsPerPage);
    dispatch(
      getAllCompletedDocAction({
        therapistUuid: providerId,
        eocId: state?.episodeOfCareId,
        signal: abortController,
        params: {
          type: activeTab,
          filter: 'COMPLETED',
          sortDirection: getSortOrder(sortBy),
          sortBy: sortBy,
          page: 0,
          pageSize: newRowsPerPage,
        },
      })
    );
    setPage(0);
  };
  const createSortHandler = (columnId: string, index: number) => {
    const isAsc = sortOrderArray[index] === 'asc';
    const newSortOrderArray = ['desc', 'desc', 'desc', 'desc', 'desc'];
    newSortOrderArray[index] = isAsc ? 'desc' : 'asc';

    dispatch(
      getAllCompletedDocAction({
        therapistUuid: providerId,
        eocId: state?.episodeOfCareId,
        signal: abortController,
        params: {
          type: activeTab,
          filter: 'COMPLETED',
          sortDirection: getSortOrder1(columnId, newSortOrderArray),
          sortBy: columnId,
          page: page,
          pageSize: rowPerPage,
        },
      })
    );
    setSortArray(newSortOrderArray);
    setSortBy(columnId);
  };

  useEffect(() => {
    if (!activeTab) return;
    setSortArray(['desc', 'desc', 'desc', 'desc', 'desc']);
    setSortBy('dateOfService');
    setPage(0);
    setRowPerPage(10);
  }, [activeTab]);

  const { patients, patientDetails, episodeOfCare, details, previewDocument } = paths;
  const navigate = useNavigate();
  const getUndoSignDocResponse = useSelector((state: any) => state.editSignDocumentReducer);
  const undoDocumentSignResponse = useSelector((state: any) => state.undoDocumentSignReducer);
  const userProviderUuid = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  const { READ_RECERTIFICATION, READ_EVAL } = permissions;

  const setPreviewDocument = (document: any) => {
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}${previewDocument}`, {
      state: {
        document,
        episodeOfCareId,
        patientData,
      },
    });
  };
  const [removeSign, setRemoveSign] = useState('');
  const removedSignDocument = (document: any) => {
    setRemoveSign(document.uuid);
    if (document?.isReformSignature && isSingleSignatureWorkflow) {
      dispatch(
        undoDocumentSignAction({
          formSubmissionUuid: document.uuid,
        })
      );
      return
    } else {
      dispatch(
        editSignDocAction(document.uuid, {
          submissionId: document.submissionId,
          isForSupervisor: Boolean(document?.supervisorId),
          formType: document?.formType,
        })
      );
    }


  };

  const checkUndoPermission = (document: any) => {
    if (document.providerId === userProviderUuid) {
      return true;
    }
    return false;
  };

  const checkReadPermission = (document: any) => {
    if (
      document.formType?.includes('RECERTIFICATION') &&
      !permissionData.includes(READ_RECERTIFICATION)
    ) {
      return false;
    }
    if (document.formType?.includes('EVALUATION') && !permissionData.includes(READ_EVAL)) {
      return false;
    }
    return true;
  };

  const checkCoSignee = (document: any) => {
    if (
      document.formType?.includes('RECERTIFICATION') ||
      document.formType?.includes('EVALUATION')
    ) {
      return false;
    }
    return true;
  };

  const onSelectAllClickCompletedDoc = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsFilterApply(false);
    if (event.target.checked) {
      setDocumentationDoc((prev: any) => ({
        ...prev,
        completedDoc: completedDocuments,
      }));
      return;
    }
    setDocumentationDoc((prev: any) => ({
      ...prev,
      completedDoc: [],
    }));
  };
  const handleNavigateToDailySessionNote = (document: any) => {
    const selectedPatientData = {
      name: patientData?.name,
      uuid: patientData?.uuid,
      firstName: patientData?.firstName,
      lastName: patientData?.lastName,
      notesId: patientData?.dailySessionNoteUuid,
    };
    navigate('/daily-session-notes', {
      state: {
        document: {
          dateOfService: document?.dateOfService,
          source: 'FROM_EOC',
          dailySessionNoteUuid: document?.dailySessionNoteUuid,
          patientEoc: selectedPatientData,
        },
      },
    });
  };

  return (
    <Box
      // sx={{ border: '1px solid #F2F4F7', borderRadius: '20px' }}
      p={3}
    >
      <Card>
        <Grid
          container
          alignItems={'center'}
          p={2}
        >
          <Typography variant="h6">Completed Documents</Typography>

          <Chip
            label={`${
              getAllCompletedDoc?.response?.totalElements
                ? getAllCompletedDoc?.response?.totalElements
                : '0'
            } Document`}
            variant="outlined"
            sx={{ marginLeft: '10px' }}
          />
        </Grid>
        <Divider />
        <Scrollbar>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                {columns?.map((column: any, index: any) => (
                  <TableCell
                    key={column.id}
                    align={column?.align}
                  >
                    {!notSortingHeader.includes(column?.id) ? (
                      <>
                        {openPrintMode && column?.id === 'documentType' ? (
                          <Checkbox
                            color="primary"
                            indeterminate={
                              documentationDoc?.completedDoc?.length > 0 &&
                              documentationDoc?.completedDoc?.length < completedDocuments?.length
                            }
                            checked={
                              completedDocuments?.length > 0 &&
                              documentationDoc?.completedDoc?.length === completedDocuments?.length
                            }
                            onChange={onSelectAllClickCompletedDoc}
                            inputProps={{
                              'aria-label': 'select all desserts',
                            }}
                          />
                        ) : null}
                        <TableSortLabel
                          active={true}
                          direction={sortOrderArray[index]}
                          onClick={() => createSortHandler(column.id, index)}
                        >
                          {column.label}
                        </TableSortLabel>
                      </>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {getAllCompletedDoc?.loader ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      textAlign="center"
                      p={3}
                    >
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                      <Skeleton animation="wave" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : getAllCompletedDoc?.response?.totalElements ? (
                completedDocuments.map((document: any) => (
                  <TableRow
                    // hover
                    key={document.uuid}
                    sx={{
                      backgroundColor: documentationDoc?.completedDoc?.some(
                        (item: any) => item?.uuid === document?.uuid
                      )
                        ? '#EBEEFE'
                        : null,
                    }}
                    onClick={() => {
                      if (openPrintMode) {
                        setIsFilterApply(false);
                        const chipIndex = documentationDoc?.completedDoc?.find(
                          (chip: any) => chip?.uuid === document?.uuid
                        );
                        if (chipIndex) {
                          const updatedChips = documentationDoc?.completedDoc?.filter(
                            (chip: any) => chip?.uuid !== document?.uuid
                          );

                          setDocumentationDoc((prev: any) => ({
                            ...prev,
                            completedDoc: updatedChips,
                          }));
                          return;
                        } else {
                          setDocumentationDoc((prev: any) => ({
                            ...prev,
                            completedDoc: [...documentationDoc?.completedDoc, document],
                          }));
                        }
                        return;
                      }
                    }}
                  >
                    <TableCell>
                      <Grid
                        container
                        alignItems="center"
                        direction="row"
                        // spacing={1}
                      >
                        {/* <ProgressNote /> */}
                        {openPrintMode ? (
                          <Checkbox
                            checked={documentationDoc?.completedDoc?.some(
                              (item: any) => item?.uuid === document?.uuid
                            )}
                          />
                        ) : null}
                        <Typography
                          variant="subtitle2"
                          sx={{ marginLeft: '4px' }}
                        >
                          {document.documentType}
                        </Typography>
                      </Grid>
                    </TableCell>
                    <TableCell>
                      {document.dateOfService
                        ? format(
                            new Date(
                              new Date(document.dateOfService).valueOf() +
                                new Date(document.dateOfService).getTimezoneOffset() * 60 * 1000
                            ),
                            'dd MMM, yyyy'
                          )
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {!document?.signedBy || document?.signedBy?.length === 0
                          ? '-'
                          : document?.signedBy[0]}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ cursor: 'pointer' }}>
                      {checkCoSignee(document) ? (
                        <Typography variant="subtitle2">
                          {document?.supervisorName || '-'}
                        </Typography>
                      ) : (
                        <Typography variant="subtitle2">-</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ cursor: 'pointer' }}>
                      {!checkCoSignee(document) ? (
                        <Typography variant="subtitle2">
                          {document?.supervisorName || '-'}
                        </Typography>
                      ) : (
                        <Typography variant="subtitle2">-</Typography>
                      )}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{ position: 'relative' }}
                    >
                      {document?.formType === 'DAILY_SOAP_NOTE' &&
                        document?.providerId === providerId && (
                          <Tooltip
                            placement="top"
                            title="Go to session notes"
                          >
                            <IconButton onClick={() => handleNavigateToDailySessionNote(document)}>
                              <TurnRightOutlined />
                            </IconButton>
                          </Tooltip>
                        )}
                      {checkReadPermission(document) && (
                        <IconButton
                          onClick={() => setPreviewDocument(document)}
                          title="View Document"
                        >
                          <VisibilityOutlined />
                        </IconButton>
                      )}
                      {checkUndoPermission(document) && (
                        <IconButton
                          onClick={() => removedSignDocument(document)}
                          title="Undo Signature"
                        >
                          {(undoDocumentSignResponse.loading || getUndoSignDocResponse.loading) &&
                          removeSign === document?.uuid ? (
                            <img
                              src={loader}
                              alt="loading"
                              style={{ height: 40 }}
                            />
                          ) : (
                            <RestoreOutlinedIcon />
                          )}
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box
                      textAlign="center"
                      p={3}
                    >
                      <Typography
                        color={'text.secondary'}
                        variant="subtitle2"
                      >
                        No Records Found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Scrollbar>
        <TablePagination
          component="div"
          count={getAllCompletedDoc?.response?.totalElements || 0}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          page={page}
          rowsPerPage={rowPerPage}
          rowsPerPageOptions={[10, 15, 30]}
        />
      </Card>
    </Box>
  );
};

export default CompletedDocument;
